// 鶏「終わらぬ鬨」の宙廻り (roosterAerialRoam・ボスラボ専用・2026-10-18)。
// もりしさん (10-17 原文)「飛んでから左右に飛び回りながら振らせてくる系の攻撃も入れたい」。
// 設計＝docs/research/2026-10-18-rooster-aerial-and-crow-art-design.md §1。数値はすべて仮。
//
// 流れ (溜め telegraph の間だけこの台本が x・高さ・向きを決める):
//   飛び上がり → 宙の点から宙の点へ左右に2回跳ぶ → 最後の点で止まって目が白く光る → 主人公へ斜め下に突っ込む
//   → 着いた瞬間に active (1発目・弾ける) → 普通の連撃の2発目 (前へ走って爪・弾ける) → 戻り (斬れる隙)。
// 宙にいる間 (高さ > AIRBORNE_UNHITTABLE_PX=72) は主人公の攻撃が当たらない (src/combat.ts の高さの判定がそのまま効く)。
// 宙では当たりを出さない (当たりは active に入ってから普通の近接で出る)。
// 覚醒王の大暴れの台本 (awoken_rampage_choreo.ts / awoken_rampage_v5.ts) とは別物。あちらの表・関数は使わない。
import type { AttackDef, Boss, Player } from './types'

type Bounds = { leftWall: number; rightWall: number }

/** 宙廻りの定数表 (1か所)。秒・px。 */
export const ROOSTER_ROAM = {
  // ① 飛び上がり: その場で真上へ。高さは easeOut (翼を打って一気に上がり、頂で緩む)。
  riseSec: 0.45,
  riseHeightPx: 200,
  // ② 宙を跳ぶ (1幕は2回)。1回＝止まり hold → 移動 travel。高さは前の点の高さ→この点の高さを結び、arc の山を足す。
  //   行き先 x は跳ぶ直前 (止まりの頭) に、主人公の今の x から offsetPx 離れた反対側。部屋の中 (xMin〜xMax) に収める。
  //   山の高さ: 200→175 で山 40 だと頂は約 228、175→190 だと約 223 (どちらも 230 以下)。
  hops: [
    { holdSec: 0.12, travelSec: 0.38, heightPx: 175, arcPx: 40, offsetPx: 240 },
    { holdSec: 0.12, travelSec: 0.38, heightPx: 190, arcPx: 40, offsetPx: 210 },
  ],
  // ③ 降りる一撃: 最後の点で止まる (この間に目が白く光る＝覚醒王の白いピカンと同じ考え・落ちる床が淡く光る)
  //   → 着地点へ一直線に突っ込む (速さは ease-in＝だんだん速く。距離で速さは変わるが、届く時刻は立ち位置によらず一定)。
  divePauseSec: 0.35,
  diveSec: 0.30,
  // 着地点 = 突っ込み始めの瞬間の主人公の x から、鶏の側へ landGapPx 手前 (爪の届く間合い 140 の内側)。
  landGapPx: 60,
  // 宙の点を置ける範囲 (部屋の中)。ボスの歩ける範囲 (bossBodyBoundsFor) でも更に絞る。
  xMin: 160,
  xMax: 800,
  // 反対側が部屋の外にはみ出して主人公との間がこれより狭くなる時は、同じ側の (今の点から遠い方の) 点へ跳ぶ。
  minPlayerGapPx: 200,
  maxPlayerGapPx: 260,
} as const

export type RoosterRoamLeg = 'rise' | 'hold' | 'hop' | 'divePause' | 'dive' | 'landed'

export type RoosterRoamPlan = { startX: number; hopXs: readonly number[]; landX?: number }

export type RoosterRoamPose = {
  x: number
  heightPx: number
  leg: RoosterRoamLeg
  /** hold / hop の何回目か (0始まり)。それ以外は -1。 */
  hopIndex: number
  /** この区間の中の進み 0→1。 */
  progress: number
  /** 打っているか。宙では打たない。着いた瞬間 (landed) が1発目＝active の頭。 */
  striking: boolean
}

const hopStarts: number[] = (() => {
  const out: number[] = []
  let t = ROOSTER_ROAM.riseSec
  for (const h of ROOSTER_ROAM.hops) { out.push(t); t += h.holdSec + h.travelSec }
  return out
})()
/** 降りる一撃の止まりが始まる秒。 */
export const ROOSTER_ROAM_DIVE_PAUSE_AT = ROOSTER_ROAM.riseSec
  + ROOSTER_ROAM.hops.reduce((s, h) => s + h.holdSec + h.travelSec, 0)
/** 突っ込み始めの秒 (着地点はこの瞬間の主人公の位置で決まる)。 */
export const ROOSTER_ROAM_DIVE_AT = ROOSTER_ROAM_DIVE_PAUSE_AT + ROOSTER_ROAM.divePauseSec
/** 着地 (＝1発目) の秒 = 溜めの長さ。立ち位置によらず一定。 */
export const ROOSTER_ROAM_LAND_AT = ROOSTER_ROAM_DIVE_AT + ROOSTER_ROAM.diveSec
/** attacks.ts の chargeMs (溜め＝飛び上がり〜着地)。 */
export const ROOSTER_ROAM_CHARGE_MS = Math.round(ROOSTER_ROAM_LAND_AT * 1000)

/** k 回目の跳びの止まりが始まる秒。 */
export function roosterHopStartSec(k: number): number { return hopStarts[k] }

const easeOutQuad = (p: number): number => 1 - (1 - p) * (1 - p)
const easeInOut = (p: number): number => p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)
const clamp01 = (p: number): number => Math.max(0, Math.min(1, p))

function hopHeightBefore(k: number): number {
  return k === 0 ? ROOSTER_ROAM.riseHeightPx : ROOSTER_ROAM.hops[k - 1].heightPx
}
function pointX(plan: RoosterRoamPlan, k: number): number {
  // k = -1 は飛び上がった点 (出発点の真上)。まだ決まっていない点は、1つ前の点に留まる。
  let x = plan.startX
  for (let i = 0; i <= k; i++) x = plan.hopXs[i] ?? x
  return x
}

/** 経過秒 → x・高さ・どの区間か・打っているか (純関数)。 */
export function roosterRoamPoseAt(t: number, plan: RoosterRoamPlan): RoosterRoamPose {
  const R = ROOSTER_ROAM
  if (t < R.riseSec) {
    const p = clamp01(t / R.riseSec)
    return { x: plan.startX, heightPx: R.riseHeightPx * easeOutQuad(p), leg: 'rise', hopIndex: -1, progress: p, striking: false }
  }
  for (let k = 0; k < R.hops.length; k++) {
    const h = R.hops[k]
    const s = hopStarts[k]
    const fromX = pointX(plan, k - 1)
    const fromH = hopHeightBefore(k)
    if (t < s + h.holdSec) {
      return { x: fromX, heightPx: fromH, leg: 'hold', hopIndex: k, progress: clamp01((t - s) / h.holdSec), striking: false }
    }
    if (t < s + h.holdSec + h.travelSec) {
      const p = clamp01((t - s - h.holdSec) / h.travelSec)
      const toX = pointX(plan, k)
      return {
        x: fromX + (toX - fromX) * easeInOut(p),
        heightPx: fromH + (h.heightPx - fromH) * p + h.arcPx * Math.sin(Math.PI * p),
        leg: 'hop', hopIndex: k, progress: p, striking: false,
      }
    }
  }
  const lastX = pointX(plan, R.hops.length - 1)
  const lastH = R.hops[R.hops.length - 1].heightPx
  if (t < ROOSTER_ROAM_DIVE_AT) {
    return { x: lastX, heightPx: lastH, leg: 'divePause', hopIndex: -1, progress: clamp01((t - ROOSTER_ROAM_DIVE_PAUSE_AT) / R.divePauseSec), striking: false }
  }
  const landX = plan.landX ?? lastX
  if (t < ROOSTER_ROAM_LAND_AT) {
    const p = clamp01((t - ROOSTER_ROAM_DIVE_AT) / R.diveSec)
    const e = p * p   // ease-in: 斜め下へ一直線に、だんだん速く
    return { x: lastX + (landX - lastX) * e, heightPx: lastH * (1 - e), leg: 'dive', hopIndex: -1, progress: p, striking: false }
  }
  return { x: landX, heightPx: 0, leg: 'landed', hopIndex: -1, progress: 1, striking: true }
}

function roomFor(bounds: Bounds): { lo: number; hi: number } {
  return { lo: Math.max(ROOSTER_ROAM.xMin, bounds.leftWall), hi: Math.min(ROOSTER_ROAM.xMax, bounds.rightWall) }
}

/** k 回目に跳ぶ先。主人公を挟んで今の点の反対側へ。部屋に収まらず間が狭くなる時は同じ側の遠い方へ。 */
export function pickRoosterHopX(prevX: number, playerX: number, k: number, bounds: Bounds): number {
  const { lo, hi } = roomFor(bounds)
  const clamp = (v: number): number => Math.max(lo, Math.min(hi, v))
  const side = prevX >= playerX ? -1 : 1   // 今の点の反対側
  const off = ROOSTER_ROAM.hops[k].offsetPx
  const across = clamp(playerX + side * off)
  if (Math.abs(across - playerX) >= ROOSTER_ROAM.minPlayerGapPx) return across
  // 反対側に場所が無い (主人公が壁際): 同じ側で、今の点から遠い方の点 (主人公から 200〜260)。
  const near = clamp(playerX - side * ROOSTER_ROAM.minPlayerGapPx)
  const far = clamp(playerX - side * ROOSTER_ROAM.maxPlayerGapPx)
  return Math.abs(near - prevX) >= Math.abs(far - prevX) ? near : far
}

/** 着地点。主人公の今の x から鶏の側へ landGapPx 手前。 */
export function pickRoosterLandX(fromX: number, playerX: number, bounds: Bounds): number {
  const { lo, hi } = roomFor(bounds)
  const dir = playerX >= fromX ? 1 : -1
  return Math.max(lo, Math.min(hi, playerX - dir * ROOSTER_ROAM.landGapPx))
}

function dirToward(fromX: number, toX: number, keep: Boss['facing']): Boss['facing'] {
  if (Math.abs(toX - fromX) < 1) return keep
  return toX > fromX ? 'right' : 'left'
}

/**
 * telegraph の毎tick、踏み込み処理の代わりに呼ぶ (boss.ts)。
 * 構えの頭で経路を作り、跳ぶ先は各跳びの止まりの頭で、着地点は突っ込み始めで、主人公の今の位置から決める (途中で追い直さない)。
 */
export function advanceRoosterRoam(boss: Boss, player: Player, _atk: AttackDef, bounds: Bounds): Boss {
  let next = boss
  const plan0 = next.roosterRoam ?? { startX: next.x, hopXs: [] }
  const t = next.stateElapsed
  const hopXs = [...plan0.hopXs]
  let landX = plan0.landX
  for (let k = 0; k < ROOSTER_ROAM.hops.length; k++) {
    if (t >= hopStarts[k] && hopXs[k] === undefined) {
      hopXs[k] = pickRoosterHopX(k === 0 ? plan0.startX : hopXs[k - 1], player.x, k, bounds)
    }
  }
  if (t >= ROOSTER_ROAM_DIVE_AT && landX === undefined) {
    landX = pickRoosterLandX(hopXs[ROOSTER_ROAM.hops.length - 1] ?? plan0.startX, player.x, bounds)
  }
  const plan: RoosterRoamPlan = { startX: plan0.startX, hopXs, landX }
  next = { ...next, roosterRoam: { startX: plan.startX, hopXs, ...(landX !== undefined ? { landX } : {}) } }
  const pose = roosterRoamPoseAt(t, plan)
  // 向き: 上がる・止まる間は主人公を見る。跳ぶ間は跳ぶ向き。突っ込みは突っ込む向きで固定 (途中で主人公が抜けても振り向かない)。
  let facing = next.facing
  if (pose.leg === 'hop') facing = dirToward(pointX(plan, pose.hopIndex - 1), pointX(plan, pose.hopIndex), facing)
  else if (pose.leg === 'dive' || pose.leg === 'landed') facing = dirToward(pointX(plan, ROOSTER_ROAM.hops.length - 1), plan.landX ?? pose.x, facing)
  else facing = dirToward(pose.x, player.x, facing)
  return { ...next, x: Math.max(bounds.leftWall, Math.min(bounds.rightWall, pose.x)), altitudePx: Math.max(0, pose.heightPx), facing }
}

/** 宙廻りの溜めを離れたら経路を片付ける (boss.ts の毎tickの頭)。高さは settleAltitude が片付ける。 */
export function settleRoosterRoam(boss: Boss): Boss {
  if (!boss.roosterRoam) return boss
  if (boss.currentAttack?.choreography === 'roosterAerialRoam' && boss.state === 'telegraph') return boss
  const { roosterRoam: _r, ...rest } = boss
  return rest
}

function inRoosterTelegraph(boss: Boss): boolean {
  return boss.currentAttack?.choreography === 'roosterAerialRoam' && boss.state === 'telegraph'
}

/** 降りる直前の目の白い光 (描画用)。覚醒王の白いピカンと同じ絵 (drawRampageGlint) で描く。 */
export function roosterRoamGlint(boss: Boss): { color: 'white'; progress: number } | null {
  if (!inRoosterTelegraph(boss)) return null
  const t = boss.stateElapsed
  if (t < ROOSTER_ROAM_DIVE_PAUSE_AT || t >= ROOSTER_ROAM_DIVE_AT) return null
  return { color: 'white', progress: (t - ROOSTER_ROAM_DIVE_PAUSE_AT) / ROOSTER_ROAM.divePauseSec }
}

/** 前tick→今tickの間に白い光が始まったら true (予兆音を鳴らすため)。 */
export function roosterGlintStarted(before: Boss, after: Boss): boolean {
  if (!inRoosterTelegraph(after) || before.currentAttack !== after.currentAttack) return false
  const t0 = before.state === 'telegraph' ? before.stateElapsed : -Infinity
  return t0 < ROOSTER_ROAM_DIVE_PAUSE_AT && after.stateElapsed >= ROOSTER_ROAM_DIVE_PAUSE_AT
}

/** 落ちる床の光 (描画用)。止まりの間は主人公の今の位置から見込んだ着地点、突っ込み中は決まった着地点。 */
export function roosterLandingPreview(boss: Boss, playerX: number): { x: number; alpha: number } | null {
  if (!inRoosterTelegraph(boss) || !boss.roosterRoam) return null
  const t = boss.stateElapsed
  if (t < ROOSTER_ROAM_DIVE_PAUSE_AT) return null
  const plan = boss.roosterRoam
  const lastX = plan.hopXs[ROOSTER_ROAM.hops.length - 1] ?? plan.startX
  // 部屋の範囲は経路と同じ (ボスの歩ける範囲で既に絞られた点から見込むので、ここでは表の範囲だけで足りる)。
  const x = plan.landX ?? pickRoosterLandX(lastX, playerX, { leftWall: -Infinity, rightWall: Infinity })
  const p = t < ROOSTER_ROAM_DIVE_AT
    ? (t - ROOSTER_ROAM_DIVE_PAUSE_AT) / ROOSTER_ROAM.divePauseSec
    : 1
  return { x, alpha: 0.25 + 0.35 * clamp01(p) }
}
